/* Starter Pack PDP — Recharge dynamically-priced customizable bundle
 * - Loads bundle config from Recharge CDN (collections, defaults)
 * - Each chosen coffee is added as its own cart line on its own subscription plan, linked to the
 *   Recharge bundle via recharge.bundle.getDynamicBundleItems (_rc_bundle properties). Checkout,
 *   order confirmation and renewals show the coffees as individual line items.
 * - Cadence options come from the coffees' own selling plans (all coffees must share the cadence).
 * - The free mug is a one-off gift line linked to the same group (_starter_gift_group).
 * - The "Your pack" gallery slide can be hidden from the theme editor (show_pack_slide).
 */
(() => {
  const SDK_URL = 'https://static.rechargecdn.com/assets/storefront/recharge-client-1.43.0.min.js';
  let sdkPromise;

  const loadSdk = (storeIdentifier) => {
    if (!sdkPromise) {
      sdkPromise = new Promise((resolve, reject) => {
        const ready = () => {
          try {
            window.recharge.init({ storeIdentifier, appName: 'truestart-starter-pack-pdp', appVersion: '1.0.0' });
          } catch (e) { /* already initialised by another app */ }
          resolve(window.recharge);
        };
        if (window.recharge && window.recharge.bundle) return ready();
        const s = document.createElement('script');
        s.src = SDK_URL;
        s.async = true;
        s.onload = ready;
        s.onerror = () => { sdkPromise = null; reject(new Error('Recharge SDK failed to load')); };
        document.head.appendChild(s);
      });
    }
    return sdkPromise;
  };

  const track = (event, data = {}) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...data });
  };

  const formatMoney = (cents, format) => {
    const value = (cents / 100);
    const whole = value % 1 === 0;
    const amount = whole ? value.toFixed(0) : value.toFixed(2);
    return (format || '£{{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      if (key === 'amount_no_decimals') return Math.round(value).toString();
      if (key === 'amount_with_comma_separator') return amount.replace('.', ',');
      return amount;
    });
  };

  class StarterPackPDP {
    constructor(root) {
      this.root = root;
      this.cfg = JSON.parse(root.querySelector('[data-spp-config]').textContent);
      this.hasPack = this.cfg.showPack !== false && root.dataset.sppPack !== 'false';
      this.coffees = this.cfg.coffees.filter((c) => c.variantId);
      const plans = this.buildPlans();
      this.plans = plans;
      const defaultPlan = plans.find((p) => p.weeks === this.cfg.defaultWeeks) || plans[Math.floor(plans.length / 2)] || null;
      this.state = {
        c1: 0,
        c2: Math.min(1, this.coffees.length - 1),
        mode: (plans.length || this.cfg.giftMode) ? 'sub' : 'once',
        plan: defaultPlan,
        busy: false,
      };
      this.bundleData = null;
      this.$ = (s) => root.querySelector(s);
      this.$$ = (s) => Array.from(root.querySelectorAll(s));

      this.renderCadence();
      this.bind();
      this.render();
      this.fetchBundle();
      this.initGallery();
      this.initSticky();
      this.initExtras();
    }

    /* ---------- Plans + pricing (from the coffees' own selling plans) ---------- */
    buildPlans() {
      const weeksOf = (p) => Number(p.weeks) || Number((String(p.name || '').match(/(\d+)\s*week/i) || [])[1] || 0);
      this.coffees.forEach((c) => {
        c.plans = (c.plans || []).map((p) => ({ ...p, weeks: weeksOf(p) })).filter((p) => p.weeks > 0);
      });
      // Frequencies come from the Recharge bundle's own selling plans, so any plan added in Recharge shows automatically.
      const bundleWeeks = ((this.cfg.bundle && this.cfg.bundle.plans) || []).map(weeksOf).filter(Boolean);
      const allowed = bundleWeeks.length ? bundleWeeks : (this.cfg.cadenceWeeks || []).map(Number).filter(Boolean);
      const sets = this.coffees.map((c) => new Set(c.plans.map((p) => p.weeks)));
      const weeks = sets.length ? [...sets[0]].filter((w) => sets.every((set) => set.has(w)) && (!allowed.length || allowed.includes(w))) : [];
      return weeks.sort((x, y) => x - y).map((w) => ({ id: w, weeks: w, name: `Every ${w} weeks` }));
    }

    planFor(coffee, weeks) {
      // Some coffees carry two plan groups per cadence (discounted Recharge plan + a full-price one): use the cheapest.
      const matches = (coffee && coffee.plans || []).filter((p) => p.weeks === weeks);
      // Prefer the Recharge bundle's own plan for this cadence (Recharge adds it to every coffee), so the
      // discount set on the bundle in Recharge drives both the price shown here and the price in the cart.
      const bp = this.bundlePlanFor(weeks);
      const own = bp && matches.find((p) => String(p.id) === String(bp.id));
      if (own) return own;
      return matches.sort((x, y) => Number(x.price) - Number(y.price))[0] || null;
    }

    bundlePlanFor(weeks) {
      const plans = (this.cfg.bundle && this.cfg.bundle.plans) || [];
      return plans.find((p) => Number(p.weeks) === Number(weeks)) || null;
    }

    planPrice(coffee, weeks) {
      const p = this.planFor(coffee, weeks);
      return p ? Number(p.price) : Number(coffee.price || 0);
    }

    /* ---------- Recharge bundle data ---------- */
    async fetchBundle() {
      const { storeIdentifier, bundle } = this.cfg;
      try {
        const res = await fetch(`https://static.rechargecdn.com/store/${storeIdentifier}/product/2020-12/${bundle.productId}.json`);
        if (!res.ok) throw new Error(res.status);
        const json = await res.json();
        const bp = json.product && json.product.bundle_product;
        if (!bp) throw new Error('No bundle_product');
        const variant = (bp.variants || []).find((v) => String(v.external_variant_id) === String(bundle.variantId)) || bp.variants[0];
        this.bundleData = {
          itemsCount: variant.items_count || 2,
          maxPerVariant: bp.max_quantity_per_variant || null,
          collections: (variant.option_sources || []).map((o) => String(o.collection_id)),
          defaults: (variant.selection_defaults || []).map((d) => String(d.external_variant_id)),
        };
        // Apply Recharge default selection if the page hasn't been touched yet
        if (!this.touched && this.bundleData.defaults.length >= 2) {
          const i1 = this.coffees.findIndex((c) => String(c.variantId) === this.bundleData.defaults[0]);
          const i2 = this.coffees.findIndex((c) => String(c.variantId) === this.bundleData.defaults[1]);
          if (i1 > -1) this.state.c1 = i1;
          if (i2 > -1) this.state.c2 = i2;
          this.render();
        }
      } catch (e) {
        console.warn('[Starter Pack] Recharge bundle data unavailable, using fallback collections', e);
        this.bundleData = { itemsCount: 2, maxPerVariant: null, collections: (bundle.fallbackCollections || []).map((c) => c.trim()), defaults: [] };
      }
    }

    collectionFor(coffee, slot, used) {
      const cols = (this.bundleData && this.bundleData.collections) || [];
      const mine = (coffee.collections || []).map(String);
      // prefer the collection matching the slot position, then any bundle collection the product is in
      const preferred = cols[slot - 1];
      if (preferred && mine.includes(preferred) && !used.includes(preferred + ':' + coffee.variantId)) return preferred;
      return cols.find((c) => mine.includes(c)) || preferred || cols[0];
    }

    /* ---------- UI ---------- */
    bind() {
      this.$$('[data-spp-swatch]').forEach((btn) => {
        btn.addEventListener('click', () => {
          this.touched = true;
          const slot = Number(btn.dataset.slot);
          this.state[`c${slot}`] = Number(btn.dataset.index);
          this.render();
          track('strength_select', { slot, strength: this.coffees[this.state[`c${slot}`]].name });
        });
      });
      // popular pairings fill both slots in one tap
      this.$$('[data-spp-pair]').forEach((btn) => {
        btn.addEventListener('click', () => {
          this.touched = true;
          this.state.c1 = Number(btn.dataset.c1);
          this.state.c2 = Number(btn.dataset.c2);
          this.render();
          track('pairing_select', {
            pairing: btn.querySelector('.spp-pair__label').textContent.trim(),
            coffees: `${this.coffees[this.state.c1].name} + ${this.coffees[this.state.c2].name}`,
          });
        });
      });
      this.$$('[data-spp-mode]').forEach((input) => {
        input.addEventListener('change', () => {
          this.state.mode = input.value;
          this.render();
          track('purchase_mode', { mode: input.value });
        });
      });
      this.$$('[data-spp-cta]').forEach((btn) => btn.addEventListener('click', () => {
        if (btn.closest('[data-spp-sticky]')) track('sticky_cta_click');
        this.addToCart();
      }));
    }

    renderCadence() {
      // Frequency dropdown (buy box design)
      const select = this.$('[data-spp-freq]');
      if (select) {
        const tpl = (this.cfg.text && this.cfg.text.freqOption) || '[weeks] weeks';
        select.innerHTML = this.plans.map((p) => `<option value="${p.weeks}">${tpl.replace('[weeks]', p.weeks)}</option>`).join('');
        if (this.state.plan) select.value = String(this.state.plan.weeks);
        select.addEventListener('change', () => {
          const plan = this.plans.find((p) => String(p.weeks) === select.value);
          if (!plan) return;
          this.state.plan = plan;
          this.render();
          track('cadence_change', { weeks: plan.weeks });
        });
      }
      const wrap = this.$('[data-spp-cadence]');
      if (!wrap) return;
      wrap.innerHTML = '';
      this.plans.forEach((plan) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'spp-cad';
        b.textContent = `${plan.weeks} wks`;
        b.dataset.planId = plan.id;
        b.addEventListener('click', (e) => {
          e.preventDefault();
          this.state.plan = plan;
          if (this.state.mode !== 'sub') {
            this.state.mode = 'sub';
            const r = this.$('[data-spp-mode][value="sub"]');
            if (r) r.checked = true;
          }
          this.render();
          track('cadence_change', { weeks: plan.weeks });
        });
        wrap.appendChild(b);
      });
    }

    tinHTML(coffee) {
      if (!coffee) return '';
      const img = coffee.image ? `<img src="${coffee.image}" alt="${coffee.name}" loading="lazy">` : '';
      return img;
    }

    render() {
      const { c1, c2, mode, plan } = this.state;
      const a = this.coffees[c1];
      const b = this.coffees[c2];
      if (!a || !b) return;
      const fmt = this.cfg.moneyFormat;
      const oncePrice = Number(a.price || 0) + Number(b.price || 0);
      const subPrice = plan ? this.planPrice(a, plan.weeks) + this.planPrice(b, plan.weeks) : oncePrice;
      const isSub = mode === 'sub' && plan;
      const price = isSub ? subPrice : oncePrice;
      const names = `${a.name} + ${b.name}`;
      const weeks = plan ? plan.weeks : this.cfg.defaultWeeks;

      // swatches
      this.$$('[data-spp-swatch]').forEach((btn) => {
        const on = Number(btn.dataset.index) === this.state[`c${btn.dataset.slot}`];
        btn.classList.toggle('is-active', on);
        btn.setAttribute('aria-checked', on ? 'true' : 'false');
      });
      this.$$('[data-spp-pair]').forEach((btn) => {
        const p1 = Number(btn.dataset.c1);
        const p2 = Number(btn.dataset.c2);
        const on = (p1 === c1 && p2 === c2) || (p1 === c2 && p2 === c1);
        btn.classList.toggle('is-active', on);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      this.$$('[data-spp-slot-name]').forEach((el) => { el.textContent = this.coffees[this.state[`c${el.dataset.sppSlotName}`]].label; });

      // modes
      this.$$('[data-spp-mode-card]').forEach((card) => card.classList.toggle('is-active', card.dataset.sppModeCard === (isSub ? 'sub' : 'once')));
      this.$$('.spp-cad').forEach((btn) => btn.classList.toggle('is-active', plan && Number(btn.dataset.planId) === plan.id));

      const set = (sel, text) => this.$$(sel).forEach((el) => { el.textContent = text; });
      const T = this.cfg.text || {};
      set('[data-spp-sub-price]', formatMoney(subPrice, fmt));
      set('[data-spp-sub-was]', subPrice < oncePrice ? formatMoney(oncePrice, fmt) : '');
      const savingAmount = Math.max(0, oncePrice - subPrice);
      const savingPct = oncePrice ? Math.round((savingAmount / oncePrice) * 100) : 0;
      this.$$('[data-spp-sub-saving]').forEach((el) => {
        el.textContent = (T.saving || 'Save [amount] · [percent]%').replace('[amount]', formatMoney(savingAmount, fmt)).replace('[percent]', savingPct);
        el.hidden = !(savingAmount > 0);
      });
      this.$$('[data-spp-sub-details]').forEach((el) => { el.hidden = !isSub; });
      const freq = this.$('[data-spp-freq]');
      if (freq) { freq.disabled = !isSub; if (plan) freq.value = String(plan.weeks); }
      this.root.classList.toggle('spp--once', !isSub);
      this.$$('[data-spp-mode]').forEach((input) => { input.checked = input.value === (isSub ? 'sub' : 'once'); });
      set('[data-spp-under-cta]', isSub ? (T.underSub || '') : (T.underOnce || ''));
      set('[data-spp-today-price]', formatMoney(subPrice, fmt));
      set('[data-spp-once-price]', formatMoney(oncePrice, fmt));
      this.$$('[data-spp-week]').forEach((el) => { el.textContent = `Week ${weeks * Number(el.dataset.sppWeek)}`; });
      this.$$('[data-spp-week-num]').forEach((el) => { el.textContent = weeks * Number(el.dataset.sppWeekNum); });
      set('[data-spp-weeks-label]', `${weeks} weeks`);
      set('[data-spp-tl-price]', `${formatMoney(subPrice, fmt)} · free delivery`);

      // CTA + summary
      const ctaText = isSub
        ? (T.ctaSubPrice || 'Subscribe — [price]').replace('[price]', formatMoney(subPrice, fmt))
        : (T.ctaOncePrice || 'Add to basket — [price]').replace('[price]', formatMoney(oncePrice, fmt));
      set('[data-spp-cta-label]', ctaText);
      set('[data-spp-summary]', isSub ? `${names} · every ${weeks} weeks` : `${names} · one-off`);
      // price under the title follows the live pack price (compare-at is printed by Liquid)
      set('[data-spp-head-price]', formatMoney(price, fmt));

      // sticky
      set('[data-spp-sticky-name]', names);
      set('[data-spp-sticky-price]', isSub ? `${formatMoney(subPrice, fmt)} today · every ${weeks} weeks` : `One-off · ${formatMoney(oncePrice, fmt)}`);
      set('[data-spp-sticky-cta]', ctaText);
      // loyalty credit in the sticky bar follows the live pack price
      this.$$('[data-spp-sticky] [data-loyalty-price]').forEach((el) => {
        const rate = parseFloat(el.dataset.loyaltyDiscount);
        if (rate > 0) el.textContent = formatMoney(Math.round(price * rate), fmt);
      });
      const sw1 = this.$('[data-spp-sticky-sw="1"]'); if (sw1) sw1.style.background = a.color;
      const sw2 = this.$('[data-spp-sticky-sw="2"]'); if (sw2) sw2.style.background = b.color;

      // pack visual (only when the "Your pack" slide is shown) + box contents
      if (this.hasPack) {
        const t1 = this.$('[data-spp-pack-tin="1"]'); if (t1) { t1.innerHTML = this.tinHTML(a); t1.style.setProperty('--sw', a.color); }
        const t2 = this.$('[data-spp-pack-tin="2"]'); if (t2) { t2.innerHTML = this.tinHTML(b); t2.style.setProperty('--sw', b.color); }
        set('[data-spp-pack-label]', names);
        const thumb = this.$('[data-spp-thumb-pack]');
        if (thumb) thumb.innerHTML = `<i style="background:${a.color}"></i><i style="background:${b.color}"></i>`;
      }
      [[1, a], [2, b]].forEach(([n, c]) => {
        const el = this.$(`[data-spp-box-tin="${n}"]`);
        if (el) el.innerHTML = `<span class="spp-box__img">${this.tinHTML(c)}</span><span>${c.name} · ${c.size || ''}</span>`;
      });

      // energy chart
      this.$$('.spp-energy__col').forEach((col) => {
        const i = Number(col.dataset.index);
        col.classList.toggle('is-picked', i === c1 || i === c2);
      });

      // plan sheet
      const rows = this.$('[data-spp-sheet-rows]');
      if (rows) {
        rows.innerHTML = isSub
          ? `<div><dt>${names} + free mug</dt><dd>${formatMoney(subPrice, fmt)}</dd></div>
             <div><dt>Delivery</dt><dd>Every ${weeks} weeks</dd></div>`
          : `<div><dt>${names}</dt><dd>${formatMoney(oncePrice, fmt)}</dd></div><div><dt>One-off</dt><dd>Coffee only · no mug</dd></div>`;
      }
      this.$$('[data-spp-sticky-sw]').forEach(() => {});
      this.applyGift();
    }

    // Gift Pack (one-time only): fixed pack price from the page product, flask row stays visible
    applyGift() {
      if (!this.cfg.giftMode) return;
      const fmt = this.cfg.moneyFormat;
      const T = this.cfg.text || {};
      const set = (sel, text) => this.$$(sel).forEach((el) => { el.textContent = text; });
      const a = this.coffees[this.state.c1];
      const b = this.coffees[this.state.c2];
      const price = Number(this.cfg.giftPrice || 0);
      const cmp = Number(this.cfg.giftCompare || 0);
      const gp = formatMoney(price, fmt);
      set('[data-spp-sub-price]', gp);
      this.$$('[data-spp-sub-was]').forEach((el) => {
        el.hidden = !(cmp > price);
        if (!el.hidden) el.innerHTML = `<span class="spp-sr">Was </span>${formatMoney(cmp, fmt)}`;
      });
      this.$$('[data-spp-sub-saving]').forEach((el) => { el.hidden = true; });
      this.$$('[data-spp-sub-details]').forEach((el) => { el.hidden = false; });
      const cta = (T.ctaGift || 'Add to cart — [price]').replace('[price]', gp);
      set('[data-spp-cta-label]', cta);
      set('[data-spp-sticky-cta]', cta);
      set('[data-spp-head-price]', gp);
      set('[data-spp-sticky-price]', gp);
      if (a && b) {
        set('[data-spp-summary]', `${a.name} + ${b.name}`);
        set('[data-spp-sticky-name]', `${a.name} + ${b.name}`);
      }
      if (T.underGift) set('[data-spp-under-cta]', T.underGift);
      this.root.classList.remove('spp--once');
    }

    showError(msg) {
      this.$$('[data-spp-error]').forEach((el) => { el.textContent = msg; el.hidden = !msg; });
    }

    setBusy(busy) {
      this.state.busy = busy;
      this.$$('[data-spp-cta]').forEach((b) => { b.disabled = busy; b.classList.toggle('is-loading', busy); });
      if (busy) this.$$('[data-spp-cta-label],[data-spp-sticky-cta]').forEach((el) => { el.dataset.prev = el.textContent; el.textContent = this.cfg.text.adding; });
      else this.render();
    }

    /* ---------- Cart ---------- */
    async addToCart() {
      if (this.state.busy) return;
      this.showError('');
      const a = this.coffees[this.state.c1];
      const b = this.coffees[this.state.c2];
      if (!a || !b) {
        this.$('[data-spp-main-cta]').scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      this.setBusy(true);
      try {
        const isGift = !!this.cfg.giftMode;
        const isSub = !isGift && this.state.mode === 'sub' && this.state.plan;
        if (!isSub && !isGift) {
          // One-off purchase: just the two coffees at full price. No subscription, no Recharge bundle, no mug.
          const onceItems = [];
          [a, b].forEach((coffee) => {
            const existing = onceItems.find((it) => String(it.id) === String(coffee.variantId));
            if (existing) existing.quantity += 1;
            else onceItems.push({ id: Number(coffee.variantId), quantity: 1, properties: { [(this.cfg.text && this.cfg.text.partOf) || 'Part of']: (this.cfg.text && this.cfg.text.onceLabel) || 'Starter Pack (one-off)' } });
          });
          await this.postCart(onceItems.reverse());
          track('add_to_cart_starter_pack', { mode: 'one-off', weeks: null, coffees: `${a.name} + ${b.name}` });
          return;
        }
        if (!this.bundleData) await this.fetchBundle();
        const rc = await loadSdk(this.cfg.storeIdentifier);
        const weeks = isGift ? null : this.state.plan.weeks;
        const bundleCfg = this.cfg.bundle;
        // The same coffee picked twice becomes one selection with quantity 2
        const picks = [];
        [a, b].forEach((coffee, i) => {
          const existing = picks.find((p) => String(p.coffee.variantId) === String(coffee.variantId));
          if (existing) existing.quantity += 1;
          else picks.push({ coffee, slot: i + 1, quantity: 1 });
        });
        const used = [];
        const selections = picks.map(({ coffee, slot, quantity }) => {
          const plan = isGift ? null : this.planFor(coffee, weeks);
          if (!isGift && !plan) throw new Error(`No ${weeks}-week plan for ${coffee.name}`);
          const collectionId = this.collectionFor(coffee, slot, used);
          used.push(collectionId + ':' + coffee.variantId);
          return {
            collectionId: String(collectionId),
            externalProductId: String(coffee.productId),
            externalVariantId: String(coffee.variantId),
            quantity,
            ...(plan ? { sellingPlan: Number(plan.id) } : {}),
          };
        });
        const bundle = {
          externalProductId: String(bundleCfg.productId),
          externalVariantId: String(bundleCfg.variantId),
          selections,
        };
        if (rc.bundle && typeof rc.bundle.validateDynamicBundle === 'function') {
          const valid = rc.bundle.validateDynamicBundle(bundle);
          if (valid !== true) throw new Error(typeof valid === 'string' ? valid : 'Invalid Starter Pack selection');
        }
        let lines;
        if (rc.bundle && typeof rc.bundle.getDynamicBundleItems === 'function') {
          lines = await rc.bundle.getDynamicBundleItems(bundle, bundleCfg.handle);
        } else {
          // Fallback: same line properties the Recharge SDK generates for dynamic bundles
          const rcId = `${Math.random().toString(36).slice(2, 12)}:${bundleCfg.productId}`;
          lines = selections.map((sel) => ({
            id: Number(sel.externalVariantId),
            quantity: sel.quantity,
            selling_plan: sel.sellingPlan,
            properties: {
              _rc_bundle: rcId,
              _rc_bundle_variant: String(bundleCfg.variantId),
              _rc_bundle_parent: bundleCfg.handle,
              _rc_bundle_collection_id: sel.collectionId,
            },
          }));
        }
        const group = (lines[0] && lines[0].properties && lines[0].properties._rc_bundle) || `spp-${Date.now()}`;
        const label = isGift ? ((this.cfg.text && this.cfg.text.giftLabel) || 'Gift Pack') : ((this.cfg.text && this.cfg.text.cartLabel) || 'Starter Pack');
      const partOf = (this.cfg.text && this.cfg.text.partOf) || 'Part of';
        // "Part of: Starter Pack" is visible in the cart, checkout and order confirmation.
        lines = lines.map((line) => ({
          ...line,
          properties: { ...(line.properties || {}), [partOf]: label, _starter_pack: group, _starter_gift_group: group, _starter_pack_size: '2' },
        }));
        // Shopify lists lines added in one request newest-first, so the gift goes in first
        // to make the cart read: coffee, coffee -> free mug.
        const items = [];
        if (this.cfg.mug && this.cfg.mug.available) {
          items.push({
            id: this.cfg.mug.variantId,
            quantity: Number(this.cfg.mug.quantity) || 1,
            properties: { _starter_gift: 'true', _starter_gift_group: group, _starter_pack: group, _gift_note: isGift ? ((this.cfg.text && this.cfg.text.giftNote) || 'Part of your Gift Pack') : 'Free with your Starter Pack', [partOf]: label },
          });
        }
        items.push(...lines);
        await this.postCart(items);
        track('add_to_cart_starter_pack', { mode: isGift ? 'gift' : (isSub ? 'subscription' : 'one-off'), weeks: isSub ? this.state.plan.weeks : null, coffees: `${a.name} + ${b.name}` });
      } catch (e) {
        console.error('[Starter Pack] add to cart failed', e);
        this.showError(this.cfg.text.error);
      } finally {
        this.setBusy(false);
      }
    }

    async postCart(items) {
      // Prefer the theme's cart API: it adds, re-renders the side cart and opens it in one flow
      // (a manual add + refresh can race and show a stale "Your Basket is Empty" drawer).
      const themeCart = window.cart;
      if (themeCart && typeof themeCart.add === 'function') {
        await themeCart.add({ items }, { requestName: 'starter-pack-add' });
        return;
      }
      const res = await fetch(`${(window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || err.message || `Cart error ${res.status}`);
      }
      const cart = window.cart;
      if (cart && typeof cart.refreshCartData === 'function') {
        try {
          await cart.refreshCartData();
          if (typeof cart.refreshSections === 'function') await cart.refreshSections(undefined, { reason: 'add' });
          if (typeof cart.updateCartCount === 'function') cart.updateCartCount();
          if (typeof cart.openSideCart === 'function') { cart.openSideCart(); return; }
        } catch (e) { console.warn('[Starter Pack] cart drawer refresh failed', e); }
      }
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      window.location.href = `${(window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'}cart`;
    }

    /* ---------- Gallery ---------- */
    initGallery() {
      const track = this.$('[data-spp-gallery-track]');
      const slides = this.$$('[data-spp-slide]');
      const thumbs = this.$$('[data-spp-thumb]');
      const dots = this.$('[data-spp-gallery-dots]');
      if (!track) return;
      if (dots) dots.innerHTML = slides.map((_, i) => `<i class="${i === 0 ? 'is-active' : ''}"></i>`).join('');
      const go = (key) => {
        const slide = slides.find((s) => s.dataset.sppSlide === String(key));
        if (!slide) return;
        track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
      };
      thumbs.forEach((t) => t.addEventListener('click', () => go(t.dataset.sppThumb)));
      let raf;
      track.addEventListener('scroll', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          const i = Math.round(track.scrollLeft / track.clientWidth);
          thumbs.forEach((t, n) => t.classList.toggle('is-active', n === i));
          if (dots) Array.from(dots.children).forEach((d, n) => d.classList.toggle('is-active', n === i));
        });
      }, { passive: true });
      // selecting coffees jumps back to the pack slide (only when it is shown)
      if (this.hasPack) {
        this.$$('[data-spp-swatch], [data-spp-pair]').forEach((b) => b.addEventListener('click', () => go(0)));
      }
    }

    /* ---------- Sticky bar + sheet ---------- */
    initSticky() {
      const sticky = this.$('[data-spp-sticky]');
      const main = this.$('[data-spp-main-cta]');
      const final = document.querySelector('[data-spp-extra="footer"] .spp-final') || this.$('.spp-final');
      if (!sticky || !main || !('IntersectionObserver' in window)) return;
      const vis = new Map();
      const update = () => {
        const drawerOpen = document.documentElement.classList.contains('side-cart-open') || document.body.classList.contains('side-cart-open') || document.querySelector('[data-cart-drawer].is-open, [cart-drawer-container].is-open, [cart-drawer-container].active');
        const show = !vis.get(main) && !vis.get(final) && this.passed && !drawerOpen;
        sticky.classList.toggle('is-visible', !!show);
        sticky.classList.toggle('active', !!show);
        sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
      };
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          vis.set(e.target, e.isIntersecting);
          if (e.target === main && !e.isIntersecting) this.passed = e.boundingClientRect.top < 0;
        });
        update();
      });
      io.observe(main);
      if (final) io.observe(final);
      // scroll fallback (some mobile browsers throttle IO during momentum scroll)
      let ticking = false;
      window.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          const r = main.getBoundingClientRect();
          vis.set(main, r.bottom > 0 && r.top < window.innerHeight);
          this.passed = r.bottom < 0;
          if (final) { const f = final.getBoundingClientRect(); vis.set(final, f.top < window.innerHeight && f.bottom > 0); }
          update();
        });
      }, { passive: true });
      // hide when keyboard opens (input focus)
      document.addEventListener('focusin', (e) => { if (e.target.matches('input[type=text],input[type=email],textarea')) { sticky.classList.remove('is-visible'); sticky.classList.remove('active'); } });

      // Sticky 'Choose options': slide back up to the buy box (no add to cart)
      const stickyScroll = this.$('[data-spp-sticky-scroll]');
      if (stickyScroll) stickyScroll.addEventListener('click', (e) => {
        e.preventDefault();
        const target = this.$('.spp-hero__info .spp-step') || this.$('.spp-hero');
        if (!target) return;
        const top = target.getBoundingClientRect().top + window.scrollY - 130;
        window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
        track('sticky_cta_click');
      });

      const sheet = this.$('[data-spp-sheet]');
      const open = () => { sheet.hidden = false; requestAnimationFrame(() => sheet.classList.add('is-open')); };
      const close = () => { sheet.classList.remove('is-open'); setTimeout(() => { sheet.hidden = true; }, 200); };
      const sheetOpen = this.$('[data-spp-sheet-open]');
      if (sheetOpen) sheetOpen.addEventListener('click', open);
      this.$$('[data-spp-sheet-close]').forEach((el) => el.addEventListener('click', close));
      this.$('[data-spp-sheet-change]').addEventListener('click', () => {
        close();
        this.$('.spp-step').scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }

    /* ---------- Other sections ---------- */
    initExtras() {
      // review filters
      const chips = this.$$('[data-spp-review-chips] .spp-chip');
      const cards = this.$$('.spp-review');
      chips.forEach((chip) => chip.addEventListener('click', () => {
        chips.forEach((c) => c.classList.toggle('is-active', c === chip));
        const f = chip.dataset.filter;
        cards.forEach((card) => {
          const show = f === 'all' || (f === 'subscriber' ? card.dataset.subscriber === 'true' : card.dataset.strength === f);
          card.hidden = !show;
        });
      }));

      // science tabs (mobile)
      const tabs = this.$$('[data-spp-sci-tabs] .spp-chip');
      const panels = this.$$('.spp-sci__card');
      tabs.forEach((tab) => tab.addEventListener('click', () => {
        tabs.forEach((t) => t.classList.toggle('is-active', t === tab));
        panels.forEach((p) => p.classList.toggle('is-active', p.dataset.panel === tab.dataset.tab));
      }));

      // energy bars set coffee 1
      this.$$('.spp-energy__col').forEach((col) => col.addEventListener('click', () => {
        this.touched = true;
        this.state.c1 = Number(col.dataset.index);
        this.render();
        track('strength_select', { slot: 1, strength: this.coffees[this.state.c1].name, source: 'energy_chart' });
      }));

      // compare picker (mobile)
      const cmp = this.$('[data-spp-compare]');
      this.$$('[data-spp-compare-pick] .spp-chip').forEach((chip, _, all) => chip.addEventListener('click', () => {
        all.forEach((c) => c.classList.toggle('is-active', c === chip));
        if (cmp) cmp.dataset.activeCol = chip.dataset.col;
        track('compare_toggle', { col: chip.textContent.trim() });
      }));

      // savings slider
      const saver = this.$('[data-spp-saver]');
      if (saver) {
        const range = saver.querySelector('[data-spp-cups]');
        const cafe = parseFloat(saver.dataset.cafe) || 0;
        const ours = parseFloat(saver.dataset.ours) || 0;
        const calc = () => {
          const cups = Number(range.value);
          const saving = Math.max(0, (cafe - ours) * cups * 365);
          saver.querySelector('[data-spp-cups-label]').textContent = cups;
          saver.querySelector('[data-spp-saving]').textContent = `£${Math.round(saving).toLocaleString('en-GB')}`;
        };
        range.addEventListener('input', calc);
        calc();
      }

      // video
      const play = this.$('[data-spp-video-play]');
      if (play) play.addEventListener('click', () => {
        const wrap = this.$('[data-spp-video]');
        const tpl = wrap.querySelector('[data-spp-video-tpl]');
        wrap.innerHTML = tpl.innerHTML;
        wrap.classList.add('is-playing');
        track('video_play');
      });
    }
  }

  const init = () => document.querySelectorAll('[data-spp]').forEach((el) => {
    if (!el.__spp) el.__spp = new StarterPackPDP(el);
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', init);
})();
